import { ImageBackground, StyleSheet, Text, View, ImageSourcePropType } from 'react-native';
import Animated from 'react-native-reanimated';
import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { HERO_HEIGHT_MAX } from '@/constants/layout';
import { getHeadingFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { CoverImageEditButton } from './CoverImageEditButton';

type ColorPalette = typeof Colors.light;

export type CollapsibleHeaderProps = {
  imageSource?: ImageSourcePropType;
  backgroundColor?: string;
  currentDate: string;
  headerTranslateStyle: any;
  dateAnimatedStyle: any;
  palette: ColorPalette;
  coverImageUri?: string | null;
  onEditCoverPress?: () => void;
  editButtonAnimatedStyle?: any;
  topInset?: number;
};

/**
 * CollapsibleHeader - Sticky header that scrolls up then stops
 *
 * Header at fixed height, scrolls up via translateY, stops when only
 * minHeight is visible. Image is bottom-aligned so Fim character stays visible.
 */
export function CollapsibleHeader({
  imageSource,
  backgroundColor,
  currentDate,
  headerTranslateStyle,
  dateAnimatedStyle,
  palette,
  coverImageUri,
  onEditCoverPress,
  editButtonAnimatedStyle,
  topInset = 0,
}: CollapsibleHeaderProps) {
  const colorScheme = useColorScheme();
  const resolvedSource = coverImageUri ? { uri: coverImageUri } : imageSource;
  const hasCustomCover = Boolean(coverImageUri);
  const coverImageBackground = colorScheme === 'dark' ? '#000000' : '#FFFFFF';

  const renderContent = () => (
    <>
      <Animated.View style={[styles.dateContainer, dateAnimatedStyle]}>
        <Text
          style={[
            styles.dateText,
            {
              color: hasCustomCover ? '#FFFFFF' : palette.text,
              fontFamily: getHeadingFont(),
              textShadowColor: hasCustomCover ? 'rgba(0, 0, 0, 0.6)' : 'transparent',
              textShadowOffset: { width: 0, height: 1 },
              textShadowRadius: hasCustomCover ? 4 : 0,
            },
          ]}
        >
          {currentDate}
        </Text>
      </Animated.View>
      {onEditCoverPress && editButtonAnimatedStyle && (
        <View style={[styles.editButtonContainer, { top: topInset + Spacing.xs }]}>
          <CoverImageEditButton onPress={onEditCoverPress} animatedStyle={editButtonAnimatedStyle} />
        </View>
      )}
    </>
  );

  return (
    <Animated.View style={[styles.headerSection, headerTranslateStyle]}>
      {/* Shadow layer - needs backgroundColor for iOS shadow rendering */}
      <View style={[styles.shadowLayer, { backgroundColor: palette.background }]} />
      {backgroundColor ? (
        <View style={[styles.imageBackground, { backgroundColor }]}>
          {renderContent()}
        </View>
      ) : (
        <ImageBackground
          source={resolvedSource!}
          style={[
            styles.imageBackground,
            hasCustomCover && { backgroundColor: coverImageBackground },
          ]}
          resizeMode="cover"
          imageStyle={styles.image}
        >
          {renderContent()}
        </ImageBackground>
      )}
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  headerSection: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: HERO_HEIGHT_MAX,
    zIndex: 10,
    overflow: 'visible',
  },
  shadowLayer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 24,
    borderBottomLeftRadius: BorderRadius.xl,
    borderBottomRightRadius: BorderRadius.xl,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 12,
    elevation: 8,
  },
  imageBackground: {
    width: '100%',
    height: '100%',
    borderBottomLeftRadius: BorderRadius.xl,
    borderBottomRightRadius: BorderRadius.xl,
    overflow: 'hidden',
  },
  image: {
    position: 'absolute',
    width: '100%',
    height: HERO_HEIGHT_MAX,
    bottom: 0,
    borderBottomLeftRadius: BorderRadius.xl,
    borderBottomRightRadius: BorderRadius.xl,
  },
  editButtonContainer: {
    position: 'absolute',
    right: Spacing.lg,
  },
  dateContainer: {
    position: 'absolute',
    right: Spacing.xl,
  },
  dateText: {
    fontSize: 20,
    fontWeight: '600',
  },
});
